<template>
  <div class="space-y-2">
    <!-- Dunning Activation -->
    <div class="brand-card-lg" v-if="true">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-blue opacity-30" />
          <font-awesome-icon :icon="['fas', 'plug']" class="text-brand-blue text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Payment Recovery Setup</span>
      </div>
      <div class="mt-0 pl-16">
        <p class="font-semibold text-brand-brown text-md">Start recovering failed payments with dunning campaigns.</p>
      </div>
      <div class="mt-4 pl-16">
        <checklist-item :completed="isCustomDomainVerified">
          <span v-if="!isCustomDomainVerified">
            Add and verify a <b class="text-brand-brown">Checkout Domain</b> to host the payment recovery page. Outgoing dunning emails will link to
            this page.
          </span>
          <span v-else>
            Your payment recovery page is hosted at
            <a :href="`https://${primaryCheckoutDomain.url}/demo`" target="_blank" class="font-semibold text-brand-brown cursor-pointer underline">
              {{ primaryCheckoutDomain.url }}
            </a>
          </span>
        </checklist-item>
        <checklist-item :completed="hasCheckoutSettings">
          Configure your <a class="font-semibold text-brand-brown cursor-pointer" @click="showCheckoutSettings = true">Checkout Page Settings</a>.
        </checklist-item>
        <checklist-item :completed="isSenderDomainVerified">
          <span v-if="!isSenderDomainVerified">
            Add and verify an <b class="text-brand-brown">Email Sender Domain</b>. Outgoing dunning emails will come from addresses on this domain.
          </span>
          <span v-else>
            Your dunning campaigns can deliver emails from any address on the following domains:
            <ul>
              <li v-for="domain in verifiedSenderDomains" :key="domain.id">
                <b class="text-brand-brown">@{{ domain.url }}</b>
              </li>
            </ul>
          </span>
        </checklist-item>
      </div>
    </div>

    <!-- Checkout Page & Custom Domain -->
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-teal-200" />
          <font-awesome-icon :icon="['fas', 'magic']" class="text-teal-900 text-lg opacity-80" />
        </font-awesome-layers>
        <span class="title -mt-2">Checkout Domain</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">
          Customize your checkout experience with a custom domain.
          <a href="https://docs.churnkey.co" target="_blank" class="text-brand-blue font-semibold"> Learn more → </a>
        </p>
      </div>
      <div class="pl-16" v-loading="loading">
        <!-- Custom Domains Config -->
        <checkout-domains-config :dunning-data="dunning" :reload="reloadData" />
      </div>
    </div>

    <!-- Sender Domain Verification -->
    <div class="brand-card-lg" id="sender-domain">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-fuschia-200" />
          <font-awesome-icon :icon="['fas', 'envelope']" class="text-fuschia-600 opacity-50 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Email Sender Domain</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">
          Send payment recovery emails from your own domain.
          <a href="https://docs.churnkey.co" target="_blank" class="text-brand-blue font-semibold"> Learn more → </a>
        </p>
      </div>
      <div class="pl-16" v-loading="loading">
        <!-- Sender Domains Config -->
        <sender-domains-config :dunning-data="dunning" :reload="reloadData" />
      </div>
    </div>

    <!-- BCC Addresses -->
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-purple-200" />
          <font-awesome-icon :icon="['fas', 'share']" class="text-purple-600 opacity-50 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">BCC Dunning Emails</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">Send dunning emails to internal addresses or your CRM.</p>
      </div>
      <div class="pl-16" v-loading="loading">
        <SettingsDunningBcc />
      </div>
    </div>

    <!-- Precision Retries -->
    <div class="brand-card-lg" id="precision-retries">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-teal-200" />
          <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" class="text-teal-800 opacity-50 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Precision Retries</span>
      </div>
      <div class="mt-0 pb-2 pl-16">
        <p class="font-semibold text-brand-brown text-md">
          Maximize your recovery rate by using our custom algorithm to retry failed payments at optimal times.
        </p>
      </div>
      <div class="pl-16" v-loading="loading">
        <SettingsPrecisionRetries />
      </div>
    </div>

    <!-- Stripe Settings -->
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
          <font-awesome-icon :icon="['fas', 'credit-card']" class="text-brand-blue opacity-70 text-xl" />
        </font-awesome-layers>
        <span class="title -mt-2">Stripe Settings Update</span>
      </div>
      <div class="mt-0 pb-2 pl-16" v-loading="loading">
        <p class="font-semibold text-brand-brown text-md">Prevent duplicate messages from being sent.</p>
        <p class="text-md text-brand-medium-brown font-medium mt-1">
          Head over to your billing settings in Stripe. Disable "Send emails to customers to update failed card payment methods."
        </p>
        <a href="https://dashboard.stripe.com/settings/billing/automatic" target="blank">
          <el-button type="primary" class="mt-4">Update Stripe Settings</el-button>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { SYNC_USER_RECORD } from '@/store/action-types';
import { customDomainIsValid } from '@/helpers/domain-utils';
import ChecklistItem from '@/components/UI/ChecklistItem.vue';
import SenderDomainsConfig from '@/components/Settings/CustomDomains/SenderDomainsConfig.vue';
import CheckoutDomainsConfig from '@/components/Settings/CustomDomains/CheckoutDomainsConfig.vue';
import SettingsDunningBcc from '@/components/Settings/SettingsDunningBcc.vue';
import SettingsPrecisionRetries from '../Settings/SettingsPrecisionRetries.vue';

export default {
  name: 'DunningConfig',
  components: {
    ChecklistItem,
    SenderDomainsConfig,
    CheckoutDomainsConfig,
    SettingsDunningBcc,
    SettingsPrecisionRetries,
  },
  data() {
    return {
      loading: true,
      isActivatingDunning: false,
      checkoutPage: {
        ckSubdomain: null,
        customDomain: {
          id: null,
          url: null,
          dnsTarget: null,
          isResolving: false,
          hasSSL: false,
          status: null,
          lastChecked: null,
        },
        redirectLink: null,
      },
    };
  },

  computed: {
    dunning() {
      return this.$store.state.org?.dunning || {};
    },
    senderDomains() {
      return this.dunning?.senderDomains || [];
    },
    isSenderDomainVerified() {
      return this.senderDomains.some((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    verifiedSenderDomains() {
      return this.senderDomains.filter((domain) => domain?.returnPathVerified && domain?.dkimVerified);
    },
    redirectLink() {
      return this.dunning?.checkoutPage.redirectLink;
    },
    hasCheckoutSettings() {
      const { redirectLink, ckSubdomain } = this.dunning?.checkoutPage;
      if (!redirectLink || !ckSubdomain) return false;
      return true;
    },
    subdomain() {
      return this.dunning?.checkoutPage?.ckSubdomain;
    },
    hasSubdomain() {
      return !!this.subdomain;
    },
    primaryCheckoutDomain() {
      return this.dunning?.checkoutPage?.customDomain;
    },
    customDomains() {
      return this.dunning?.checkoutPage?.customDomains || [];
    },
    hasCustomDomain() {
      return this.customDomains.some((domain) => !!domain?.url);
    },
    isCustomDomainVerified() {
      return this.customDomains.some(customDomainIsValid);
    },
    verifiedCustomDomains() {
      return this.customDomains.filter(customDomainIsValid);
    },
    isDunningActivated() {
      return this.dunning?.enabled;
    },
    isReadyToActivate() {
      if (this.isSenderDomainVerified) {
        // Now we require both custom domain AND subdomain
        return this.isCustomDomainVerified && this.hasSubdomain;
      }
      return false;
    },
  },
  async created() {
    if (!this.$store.state.org) {
      await this.$store.dispatch(SYNC_USER_RECORD);
    }
    this.org = this.$store.state.org;
    if (this.hasSubdomain) {
      this.checkoutPage.ckSubdomain = this.subdomain;
    }
    this.loading = false;
  },
  mounted() {
    // allow to link to specific sections of the page
    const scrollWhiteList = ['sender-domain', 'precision-retries'];
    if (this.$route.query.focus && scrollWhiteList.includes(this.$route.query.focus)) {
      const el = document.getElementById(this.$route.query.focus);
      el.scrollIntoView({ behavior: 'smooth' });
    }
  },
  methods: {
    async reloadData() {
      await this.$store.dispatch(SYNC_USER_RECORD);
    },
    displayError(error) {
      if (error?.response?.data?.message) {
        this.$notify({ title: `Error: ${error.response.data.message}`, type: 'error' });
      } else {
        this.$notify({ title: 'Error. Try again or contact us via chat for help.', type: 'error' });
      }
    },
  },
};
</script>
